<template>
    
    <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <!-- Sidebar -->
<component 
  :is="layoutMap[currentModule].sidebar"
  :isOpen="isSidebarOpen" 
/>
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
       <header 
        class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm transition-all duration-300"
      >
<component 
  :is="layoutMap[currentModule].topbar"
  :page-title="currentPageTitle"
  :is-sidebar-open="isSidebarOpen"
  :is-dark-mode="isDarkMode"
  @toggle-sidebar="toggleSidebar"
  @toggle-dark-mode="toggleDarkMode"
/>
      </header>

            <div class="max-w-4xl mt-4 mb-4 mx-auto p-6 bg-white rounded-lg shadow-md mt-[100px] mb-5">

      <h1 class="text-2xl font-bold text-[#035a76] mb-6">Employee Onboarding Form</h1>

      <!-- Draft indicator -->
      <div v-if="employeeId" class="mb-4 text-sm text-[#035a76] bg-[#035a76]/5 border border-[#035a76]/20 rounded-md px-3 py-2">
        Continuing a saved draft. Anything you've already saved has been loaded below.
      </div>
      <div v-if="isLoadingDraft" class="mb-4 text-sm text-gray-500">
        Checking for a previously saved draft...
      </div>
      
      <!-- Progress Indicator -->
      <div class="mb-8">
        <div class="flex justify-between mb-2">
          <span v-for="(step, index) in steps" :key="index" 
                :class="{'font-bold': currentStep === index + 1, 'text-[#035a76]': currentStep >= index + 1}"
                class="text-sm">
            {{ step }}
          </span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-2.5">
          <div class="bg-[#76ba51] h-2.5 rounded-full" 
               :style="`width: ${(currentStep / steps.length) * 100}%`"></div>
        </div>
      </div>
  
      <form novalidate @submit.prevent="submitForm">
        <!-- Step 1: Personal Information -->
        <div v-show="currentStep === 1" class="space-y-6">
          <h2 class="text-xl font-semibold border-b pb-2">Personal Information</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700">First Name<span class="text-red-500">*</span></label>
              <input v-model="form.personal.firstName" type="text" 
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                       <p v-if="errors.firstName" class="text-red-500 text-sm mt-1">{{ errors.firstName }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Middle Name</label>
              <input v-model="form.personal.middleName" type="text"
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Last Name<span class="text-red-500">*</span></label>
              <input v-model="form.personal.lastName" type="text"
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                       <p v-if="errors.lastName" class="text-red-500 text-sm mt-1">{{ errors.lastName }}</p>
            </div>
          </div>
  
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700">Gender<span class="text-red-500">*</span></label>
              <select v-model="form.personal.gender" required
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <p v-if="errors.gender" class="text-red-500 text-sm mt-1">{{ errors.gender }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Date of Birth<span class="text-red-500">*</span></label>
              <input v-model="form.personal.dob" type="date" name="personal[dob]"
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                       <p v-if="errors.dob" class="text-red-500 text-sm mt-1">{{ errors.dob }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Place of Birth<span class="text-red-500">*</span></label>
              <input v-model="form.personal.placeOfBirth" type="text" 
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                       <p v-if="errors.placeOfBirth" class="text-red-500 text-sm mt-1">{{ errors.placeOfBirth }}</p>
            </div>
          </div>
  
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700">Nationality<span class="text-red-500">*</span></label>
              <input v-model="form.personal.nationality" type="text" 
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                       <p v-if="errors.nationality" class="text-red-500 text-sm mt-1">{{ errors.nationality }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Phone Number<span class="text-red-500">*</span></label>
              <input
  v-model="form.personal.phone"
  type="tel"
  inputmode="numeric"
  maxlength="10"
  @input="form.personal.phone = form.personal.phone.replace(/\D/g, '')"
  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
>
                       <p v-if="errors.phone" class="text-red-500 text-sm mt-1">{{ errors.phone }}</p>
            </div>
          </div>
  
          <div>
            <label class="block text-sm font-medium text-gray-700">Email Address<span class="text-red-500">*</span></label>
            <input v-model="form.personal.email" type="email"
                   class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                       <p v-if="errors.email" class="text-red-500 text-sm mt-1">{{ errors.email }}</p>
          </div>
  
          <div>
            <label class="block text-sm font-medium text-gray-700">Residential Address<span class="text-red-500">*</span></label>
            <textarea v-model="form.personal.address" rows="3"
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"></textarea>
                       <p v-if="errors.address" class="text-red-500 text-sm mt-1">{{ errors.address }}</p>
          </div>
        </div>
  
        <!-- Step 2: Marital Status & Family -->
        <div v-show="currentStep === 2" class="space-y-6">
  <h2 class="text-xl font-semibold border-b pb-2">Marital Status & Family Information</h2>
  
  <div>
    <label class="block text-sm font-medium text-gray-700 mb-2">Marital Status<span class="text-red-500">*</span></label>
    <div class="space-y-2">
      <div v-for="status in maritalStatuses" :key="status.value" class="flex items-center">
        <input :id="`marital-${status.value}`" v-model="form.marital.status" :value="status.value" 
               type="radio" required class="h-4 w-4 text-[#035a76] focus:ring-[#035a76] border-gray-300">
        <label :for="`marital-${status.value}`" class="ml-2 block text-sm text-gray-700">
          {{ status.label }}
        </label>
      </div>
      <div v-if="errors.status" class="text-red-500 text-sm mt-1">
        {{ errors.status }}
      </div>
    </div>
  </div>

  <div v-if="form.marital.status === 'married'" class="mt-4">
    <label class="block text-sm font-medium text-gray-700">Spouse Name<span class="text-red-500">*</span></label>
<input 
  v-model="form.marital.spouseName" 
  type="text"
  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
>

<p v-if="errors.spouseName" class="text-red-500 text-sm mt-1">
  {{ errors.spouseName }}
</p>

  </div>

  <div class="mt-6">
    <div class="flex items-center">
      <input id="has-children" v-model="form.marital.hasChildren" type="checkbox" 
             class="h-4 w-4 text-[#035a76] focus:ring-[#035a76] border-gray-300 rounded">
      <label for="has-children" class="ml-2 block text-sm text-gray-700">
        Do you have any children?
      </label>
    </div>

    <div v-if="form.marital.hasChildren" class="mt-4 space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700">Number of Children<span class="text-red-500">*</span></label>
        <input 
          v-model.number="form.marital.numberOfChildren" 
          type="number" 
          min="1" 
          max="20"
          @change="updateChildrenFields"
          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
      </div>

      <transition-group name="fade" tag="div">
        <div v-for="(child, index) in form.marital.children" :key="index" class="border p-4 rounded-md">
          <div class="flex justify-between items-center mb-3">
            <h3 class="text-sm font-medium">Child {{ index + 1 }}</h3>
            <button 
              v-if="form.marital.children.length > 1"
              type="button" 
              @click="removeChild(index)"
              class="text-red-600 hover:text-red-800 text-sm font-medium">
              Remove
            </button>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Full Name<span class="text-red-500">*</span></label>
              <input 
                v-model="child.fullName" 
                type="text" 
                required
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Age<span class="text-red-500">*</span></label>
              <input 
                v-model.number="child.age" 
                type="number" 
                min="0" 
                max="30"
                required
                class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>
          </div>
        </div>
      </transition-group>
      
      <button 
        type="button" 
        @click="addChild"
        class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-[#035a76] bg-[#76ba51]/10 hover:bg-[#76ba51]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Add Another Child
      </button>
    </div>
  </div>

  
          <div class="mt-6">
            <h3 class="text-lg font-medium border-b pb-2">Parents Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Father's Name/Emergency Person<span class="text-red-500">*</span></label>
                <input v-model="form.parents.fatherName" type="text"
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                       <p v-if="errors.fatherName" class="text-red-500 text-sm mt-1">{{ errors.fatherName }}</p>

              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Father's Contact/Emergency Number<span class="text-red-500">*</span></label>
<input
  v-model="form.parents.fatherContact"
  type="tel"
  inputmode="numeric"
  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
  maxlength="10"
  @input="form.parents.fatherContact = form.parents.fatherContact.replace(/\D/g, '')"
>
<p v-if="errors.fatherContact" class="text-red-500 text-sm mt-1">{{ errors.fatherContact }}</p>

              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Mother's Name/ Emergency Person<span class="text-red-500">*</span></label>
                <input v-model="form.parents.motherName" type="text"
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                       <p v-if="errors.motherName" class="text-red-500 text-sm mt-1">{{ errors.motherName }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Mother's Contact/ Emergency Number</label>
<input
  v-model="form.parents.motherContact"
  type="tel"
  inputmode="numeric"
  class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
  maxlength="10"
  @input="form.parents.motherContact = form.parents.motherContact.replace(/\D/g, '')"
>
<p v-if="errors.motherContact" class="text-red-500 text-sm mt-1">{{ errors.motherContact }}</p>

              </div>
            </div>
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700">Parents' Address</label>
              <textarea v-model="form.parents.address" required rows="2"
                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"></textarea>
            </div>
          </div>
        </div>
  
        <!-- Step 3: Work Experience -->
        <div v-show="currentStep === 3" class="space-y-6">
          <h2 class="text-xl font-semibold border-b pb-2">Work Experience</h2>
          
          <div v-for="(exp, index) in form.workExperience" :key="index" class="border p-4 rounded-md">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-sm font-medium">Previous Work {{ index + 1 }}</h3>
              <button v-if="index > 0" type="button" @click="removeWorkExperience(index)"
                      class="text-red-600 hover:text-red-800 text-sm font-medium">
                Remove
              </button>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700">Company Name<span class="text-red-500">*</span></label>
                <input v-model="exp.company" type="text" 
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Position Held<span class="text-red-500">*</span></label>
                <input v-model="exp.position" type="text" 
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Company Address<span class="text-red-500">*</span></label>
                <input v-model="exp.address" type="text" 
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Contact Person/Number</label>
                <input v-model="exp.contact" type="text"
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Start Date<span class="text-red-500">*</span></label>
                <input v-model="exp.startDate" type="date" 
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">End Date<span class="text-red-500">*</span></label>
                <input v-model="exp.endDate" type="date" 
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
            </div>
            
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700">Reason for Leaving<span class="text-red-500">*</span></label>
              <textarea v-model="exp.reasonForLeaving" required rows="2"
                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"></textarea>
            </div>
          </div>
          
          <button type="button" @click="addWorkExperience"
                  class="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-[#035a76] bg-[#76ba51]/10 hover:bg-[#76ba51]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
            <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Another Work Experience
          </button>
        </div>
  
        <!-- Step 4: Education -->
        <div v-show="currentStep === 4" class="space-y-6">
          <h2 class="text-xl font-semibold border-b pb-2">Education Background</h2>
          
          <div v-for="(edu, index) in form.education" :key="index" class="border p-4 rounded-md">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-sm font-medium">Education {{ index + 1 }}</h3>
              <button v-if="index > 0" type="button" @click="removeEducation(index)"
                      class="text-red-600 hover:text-red-800 text-sm font-medium">
                Remove
              </button>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700">Certificate/Degree<span class="text-red-500">*</span></label>
                <select v-model="edu.certificate" required
                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                  <option value="">Select Certificate</option>
                  <option value="WASSCE">WASSCE</option>
                  <option value="Diploma">Diploma</option>
                  <option value="Bachelor">Bachelor's Degree</option>
                  <option value="Master">Master's Degree</option>
                  <option value="PhD">PhD</option>
                  <option value="Other">Other</option>
                </select>
                <input v-if="edu.certificate === 'Other'" v-model="edu.otherCertificate" type="text" required
                       class="mt-2 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">School Name<span class="text-red-500">*</span></label>
                <input v-model="edu.school" type="text" required
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Start Year<span class="text-red-500">*</span></label>
                <input v-model="edu.startYear" type="number" min="1900" :max="new Date().getFullYear()" :name="`education[${index}][startYear]`" required
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">End Year<span class="text-red-500">*</span></label>
                <input v-model="edu.endYear" type="number" min="1900" :max="new Date().getFullYear()" :name="`education[${index}][endYear]`" required
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
              </div>
            </div>
          </div>
          
          <button type="button" @click="addEducation"
                  class="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-[#035a76] bg-[#035a76]/10 hover:bg-[#035a76]/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
            <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Another Education
          </button>
        </div>
  
        <!-- Step 5: Emergency Contact & Documents -->
        <div v-show="currentStep === 5" class="space-y-6">
          <h2 class="text-xl font-semibold border-b pb-2">Emergency Contact</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700">Full Name<span class="text-red-500">*</span></label>
              <input v-model="form.emergencyContact.fullName" type="text" required
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                <p v-if="errors.emergencyFullName" class="text-red-500 text-sm mt-1">{{ errors.emergencyFullName }}</p>     
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Relationship<span class="text-red-500">*</span></label>
              <input v-model="form.emergencyContact.relationship" type="text" required
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                                                 <p v-if="errors.emergencyRelationship" class="text-red-500 text-sm mt-1">{{ errors.emergencyRelationship }}</p>     

            </div>

          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700">Phone Number<span class="text-red-500">*</span></label>
              <input v-model="form.emergencyContact.phone" type="tel" required
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
<p v-if="errors.emergencyPhone" class="text-red-500 text-sm mt-1">{{ errors.emergencyPhone }}</p> 
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Alternative Phone</label>
              <input v-model="form.emergencyContact.altPhone" type="tel"
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700">Address<span class="text-red-500">*</span></label>
            <textarea v-model="form.emergencyContact.address" required rows="2"
                      class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"></textarea>
          </div>
  
          <h2 class="text-xl font-semibold border-b pb-2 mt-8">Bank Details</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700">Bank Name<span class="text-red-500">*</span></label>
              <input v-model="form.bank.bankName" type="text"
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>
             <div>
              <label class="block text-sm font-medium text-gray-700">Branch Name<span class="text-red-500">*</span></label>
              <input v-model="form.bank.branchName" type="text" 
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>           
            <div>
              <label class="block text-sm font-medium text-gray-700">Account Number<span class="text-red-500">*</span></label>
              <input v-model="form.bank.accountNumber" type="text" 
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700">Account Holder Name<span class="text-red-500">*</span></label>
            <input v-model="form.bank.accountName" type="text" 
                   class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
          </div>
  
          <h2 class="text-xl font-semibold border-b pb-2 mt-8">Security Information</h2>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label class="block text-sm font-medium text-gray-700">Ghana Card Number<span class="text-red-500">*</span></label>
              <input v-model="form.security.ghanaCardNumber" type="text" 
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Ghana Card Expiry Date<span class="text-red-500">*</span></label>
              <input v-model="form.security.ghanaCardExpiry" type="date" 
                     class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700">SSNIT Number<span class="text-red-500">*</span></label>
            <input v-model="form.security.ssnitNumber" type="text" 
                   class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
          </div>
  
          <h2 class="text-xl font-semibold border-b pb-2 mt-8">Document Uploads</h2>
          
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Curriculum Vitae (CV)<span class="text-red-500">*</span></label>
              <input type="file" @change="handleFileUpload('cv', $event)" accept=".pdf,.doc,.docx" required
                     class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20">
              <p class="mt-1 text-xs text-gray-500">PDF or Word document (Max 5MB)</p>
              <p v-if="existingDocuments.cv" class="mt-1 text-xs text-[#035a76]">
                Already on file: {{ existingDocuments.cv.fileName }} — choose a new file only if you want to replace it.
              </p>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Ghana Card (Front)<span class="text-red-500">*</span></label>
                <input type="file" @change="handleFileUpload('ghanaCardFront', $event)" accept="image/*,.pdf" 
                       class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20">
                <p class="mt-1 text-xs text-gray-500">Image or PDF (Max 5MB)</p>
                <p v-if="existingDocuments.ghanaCardFront" class="mt-1 text-xs text-[#035a76]">
                  Already on file: {{ existingDocuments.ghanaCardFront.fileName }}
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Ghana Card (Back)<span class="text-red-500">*</span></label>
                <input type="file" @change="handleFileUpload('ghanaCardBack', $event)" accept="image/*,.pdf" 
                       class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20">
                <p class="mt-1 text-xs text-gray-500">Image or PDF (Max 5MB)</p>
                <p v-if="existingDocuments.ghanaCardBack" class="mt-1 text-xs text-[#035a76]">
                  Already on file: {{ existingDocuments.ghanaCardBack.fileName }}
                </p>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-700">NHIS Card<span class="text-red-500">*</span></label>
              <input type="file" @change="handleFileUpload('nhisCard', $event)" accept="image/*,.pdf" 
                     class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20">
              <p class="mt-1 text-xs text-gray-500">Image or PDF (Max 5MB)</p>
              <p v-if="existingDocuments.nhisCard" class="mt-1 text-xs text-[#035a76]">
                Already on file: {{ existingDocuments.nhisCard.fileName }}
              </p>
            </div>
          </div>
        </div>

        <div v-show="currentStep === 6" class="space-y-6">
          <h2 class="text-xl font-semibold border-b pb-2">Employee Signature</h2>

            <div class="border rounded-lg p-4 bg-gray-50">
    <canvas
      ref="signatureCanvas"
      class="w-full h-48 border rounded bg-white"
    ></canvas>
  </div>

  <div class="flex gap-3">
    <button
      type="button"
      @click="clearSignature"
      class="px-4 py-2 bg-gray-500 text-white rounded"
    >
      Clear
    </button>

    <!-- <button
      type="button"
      @click="saveSignature"
      class="px-4 py-2 bg-blue-600 text-white rounded"
    >
      Save Signature
    </button> -->
  </div>

        </div>
  
        <!-- Navigation Buttons -->
        <div class="flex justify-between items-center mt-8">
          <button v-if="currentStep > 1" type="button" @click="prevStep"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
            Previous
          </button>
          <div v-else></div> <!-- Empty div for spacing -->

          <div class="flex items-center gap-3">
            <span v-if="stepMessages[currentStep]"
                  :class="stepMessages[currentStep].type === 'error' ? 'text-red-500' : 'text-green-600'"
                  class="text-sm">
              {{ stepMessages[currentStep].text }}
            </span>

            <button v-if="currentStep < steps.length" type="button" @click="saveStep(currentStep)"
                    :disabled="savingStep === currentStep"
                    class="inline-flex items-center px-4 py-2 border border-[#035a76] shadow-sm text-sm font-medium rounded-md text-[#035a76] bg-white hover:bg-[#035a76]/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76] disabled:opacity-50 disabled:cursor-not-allowed">
              {{ savingStep === currentStep ? 'Saving...' : (employeeId ? 'Update' : 'Save') }}
            </button>

            <button v-if="currentStep < steps.length" type="button" @click="nextStep"
                    class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
              Next
            </button>

            <button v-else type="submit"
                    :disabled="isSubmitting"
                    class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
              {{ isSubmitting ? 'Submitting...' : 'Submit Onboarding' }}
            </button>
          </div>
        </div>
      </form>
      </div>
      </main>
<Teleport to="body">
  <div v-if="showSuccessModal" 
       class="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
       @click.self="showSuccessModal = false">
    <div class="bg-white p-6 rounded-lg text-center max-w-sm shadow-xl">
      <h3 class="text-xl font-semibold text-[#035a76] mb-2">✅ Submitted Successfully</h3>
      <p class="text-gray-600 mb-4">Your form has been submitted.</p>
      <button @click="showSuccessModal = false"
              class="px-5 py-2 bg-[#76ba51] text-white rounded-md hover:bg-[#76ba51]/90">
        Close
      </button>
    </div>
  </div>
</Teleport>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, nextTick,computed } from 'vue';
  import api from '@/services/api';
import HrSidebar from '@/components/hr/HrSidebar.vue'
import AcdSidebar from '@/components/acd/AcdSidebar.vue'
import CommSidebar from '@/components/com/ComSidebar.vue'
// import ProcSidebar from '@/components/ProcSidebar.vue'

import HrTopbar from '@/components/hr/HrTopbar.vue'
import AcdTopbar from '@/components/acd/AcdTopbar.vue'
import CommTopbar from '@/components/com/ComTopbar.vue'
// import ProcTopbar from '@/components/ProcTopbar.vue'
import ItSidebar from '@/components/it/ItSidebar.vue';
import ItTopbar from '@/components/it/ItTopbar.vue';


  import SignaturePad from "signature_pad";
  import { useRoute } from 'vue-router'


  const route = useRoute()

  const layoutMap = {
  hr: {
    sidebar: HrSidebar,
    topbar: HrTopbar
  },
  academics: {
    sidebar: AcdSidebar,
    topbar: AcdTopbar
  },
  // it:{
  //   sidebar: ItSidebar,
  //   topbar: ItTopbar
  // },
  it: {
    sidebar: ItSidebar,
    topbar: ItTopbar
  },
    com: {
    sidebar: CommSidebar,
    topbar: CommTopbar
  },
  // procurement: {
  //   sidebar: ProcSidebar,
  //   topbar: ProcTopbar
  // }
}

const currentModule = computed(() => {
  if (route.path.startsWith('/academics')) return 'academics'
  if(route.path.startsWith('/it')) return 'it'
  if (route.path.startsWith('/com')) return 'com'
  if (route.path.startsWith('/procurement')) return 'procurement'
  return 'hr'
})

const signatureCanvas = ref(null);
let signaturePad = null;

const signatureData = ref(null);



    const currentPageTitle = ref('Onboarding');

  
  const isSidebarOpen = ref(true);
  const isDarkMode = ref(false);
  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
  };
  
  const toggleDarkMode = () => {
    isDarkMode.value = !isDarkMode.value;
    if (isDarkMode.value) {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  };
  
  const steps = [
    'Personal Info',
    'Family Info',
    'Work Experience',
    'Education',
    'Contacts & Docs',
    'Signature'
  ];
  
  const currentStep = ref(1);
  
  const maritalStatuses = [
    { value: 'single', label: 'Single' },
    { value: 'married', label: 'Married' },
    { value: 'divorced', label: 'Divorced' },
    { value: 'widowed', label: 'Widowed' }
  ];
  
  const form = ref({
    personal: {
      firstName: '',
      middleName: '',
      lastName: '',
      gender: '',
      dob: '',
      placeOfBirth: '',
      nationality: '',
      address: '',
      phone: '',
      email: ''
    },
    marital: {
      status: '',
      spouseName: '',
      hasChildren: false,
      numberOfChildren: 1,
      children: [{ fullName: '', age: '' }]
    },
    parents: {
      fatherName: '',
      fatherContact: '',
      motherName: '',
      motherContact: '',
      address: ''
    },
    workExperience: [
      {
        company: '',
        position: '',
        address: '',
        contact: '',
        startDate: '',
        endDate: '',
        reasonForLeaving: ''
      }
    ],
    education: [
      {
        certificate: '',
        otherCertificate: '',
        school: '',
        startYear: '',
        endYear: ''
      }
    ],
    emergencyContact: {
      fullName: '',
      relationship: '',
      phone: '',
      altPhone: '',
      address: ''
    },
    bank: {
      bankName: '',
      branchName: '',
      accountNumber: '',
      accountName: ''
    },
    security: {
      ghanaCardNumber: '',
      ghanaCardExpiry: '',
      ssnitNumber: ''
    },
    documents: {
      cv: null,
      ghanaCardFront: null,
      ghanaCardBack: null,
      nhisCard: null
    }
  });
  
  // const nextStep = () => {
  //   if (currentStep.value < steps.length) {
  //     currentStep.value++;
  //   }
  // };
  const nextStep = () => {
  if (!validateCurrentStep()) return;
  if (currentStep.value < steps.length) currentStep.value++;
};

const errors = ref({
  firstName: '',
  lastName: '',
  gender: '',
  dob: '',
  placeOfBirth: '',
  nationality: '',
  address: '',
  phone: '',
  email: '',

  status: '',
  spouseName: '',
  fatherName: '',
  fatherContact: '',
  motherName: '',
  motherContact: '',
  parentsAddress: '',

  emergencyFullName: '',
  emergencyRelationship: '',
  emergencyPhone: '',
  emergencyAltPhone: '',
  emergencyAddress: '',

  bankName: '',
  branchName: '',
  accountNumber: '',
  accountName: '',

  ghanaCardNumber: '',
  ghanaCardExpiry: '',
  ssnitNumber: '',

  cv: '',
  ghanaCardFront: '',
  ghanaCardBack: '',
  nhisCard: ''
});

// ============================================================
// FRONTEND VALIDATION HELPERS
// ============================================================

const clearErrors = () => {
  Object.keys(errors.value).forEach(key => {
    errors.value[key] = '';
  });
};

const isEmpty = (value) => {
  return value === null || value === undefined || String(value).trim() === '';
};

const isValidName = (value) => {
  // Allows letters, spaces, apostrophes and hyphens
  return /^[A-Za-zÀ-ÖØ-öø-ÿ' -]+$/.test(value.trim());
};

const isValidPhone = (value) => {
  // Exactly 10 digits
  return /^\d{10}$/.test(String(value).trim());
};

const isValidEmail = (value) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
};

const isValidDate = (value) => {
  if (!value) return false;

  const date = new Date(value);

  return !isNaN(date.getTime());
};

const isFutureDate = (value) => {
  if (!value) return false;

  const selectedDate = new Date(value);
  const today = new Date();

  today.setHours(0, 0, 0, 0);
  selectedDate.setHours(0, 0, 0, 0);

  return selectedDate > today;
};

const isValidYear = (value) => {
  const year = Number(value);

  return Number.isInteger(year) &&
         year >= 1900 &&
         year <= new Date().getFullYear();
};

const isValidAccountNumber = (value) => {
  return /^\d+$/.test(String(value).trim());
};

const validateFile = (file, field, allowedTypes = []) => {
  if (!file) {
    errors.value[field] = 'Please select a file.';
    return false;
  }

  // 2 MB
  const MAX_FILE_SIZE = 2 * 1024 * 1024;

  if (file.size > MAX_FILE_SIZE) {
    errors.value[field] = 'File size must not exceed 2 MB.';
    return false;
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    errors.value[field] = 'Invalid file type.';
    return false;
  }

  return true;
};

const validateCurrentStep = () => {
  clearErrors();

  let valid = true;

  // ============================================================
  // STEP 1 - PERSONAL INFORMATION
  // ============================================================

  if (currentStep.value === 1) {

    // First Name
    if (isEmpty(form.value.personal.firstName)) {
      errors.value.firstName = 'First name is required.';
      valid = false;
    } else if (!isValidName(form.value.personal.firstName)) {
      errors.value.firstName =
        'First name can only contain letters, spaces, hyphens or apostrophes.';
      valid = false;
    }

    // Last Name
    if (isEmpty(form.value.personal.lastName)) {
      errors.value.lastName = 'Last name is required.';
      valid = false;
    } else if (!isValidName(form.value.personal.lastName)) {
      errors.value.lastName =
        'Last name can only contain letters, spaces, hyphens or apostrophes.';
      valid = false;
    }

    // Gender
    if (isEmpty(form.value.personal.gender)) {
      errors.value.gender = 'Please select a gender.';
      valid = false;
    }

    // Date of Birth
    if (isEmpty(form.value.personal.dob)) {
      errors.value.dob = 'Date of birth is required.';
      valid = false;
    } else if (!isValidDate(form.value.personal.dob)) {
      errors.value.dob = 'Please enter a valid date of birth.';
      valid = false;
    } else if (isFutureDate(form.value.personal.dob)) {
      errors.value.dob = 'Date of birth cannot be in the future.';
      valid = false;
    }

    // Place of Birth
    if (isEmpty(form.value.personal.placeOfBirth)) {
      errors.value.placeOfBirth = 'Place of birth is required.';
      valid = false;
    }

    // Nationality
    if (isEmpty(form.value.personal.nationality)) {
      errors.value.nationality = 'Nationality is required.';
      valid = false;
    }

    // Address
    if (isEmpty(form.value.personal.address)) {
      errors.value.address = 'Residential address is required.';
      valid = false;
    }

    // Phone
    if (isEmpty(form.value.personal.phone)) {
      errors.value.phone = 'Phone number is required.';
      valid = false;
    } else if (!isValidPhone(form.value.personal.phone)) {
      errors.value.phone = 'Phone number must contain exactly 10 digits.';
      valid = false;
    }

    // Email
    if (isEmpty(form.value.personal.email)) {
      errors.value.email = 'Email address is required.';
      valid = false;
    } else if (!isValidEmail(form.value.personal.email)) {
      errors.value.email = 'Please enter a valid email address.';
      valid = false;
    }
  }


  // ============================================================
  // STEP 2 - FAMILY
  // ============================================================

  if (currentStep.value === 2) {

    // Marital status
    if (isEmpty(form.value.marital.status)) {
      errors.value.status = 'Please select your marital status.';
      valid = false;
    }

    // Spouse
    if (form.value.marital.status === 'married') {

      if (isEmpty(form.value.marital.spouseName)) {
        errors.value.spouseName = 'Spouse name is required.';
        valid = false;
      } else if (!isValidName(form.value.marital.spouseName)) {
        errors.value.spouseName = 'Please enter a valid spouse name.';
        valid = false;
      }
    }

    // Children
    if (form.value.marital.hasChildren) {

      const numberOfChildren =
        Number(form.value.marital.numberOfChildren);

      if (
        !Number.isInteger(numberOfChildren) ||
        numberOfChildren < 1 ||
        numberOfChildren > 20
      ) {
        alert('Number of children must be between 1 and 20.');
        valid = false;
      }

      form.value.marital.children.forEach((child, index) => {

        if (isEmpty(child.fullName)) {
          alert(`Child ${index + 1}: Full name is required.`);
          valid = false;
        } else if (!isValidName(child.fullName)) {
          alert(`Child ${index + 1}: Please enter a valid name.`);
          valid = false;
        }

        const age = Number(child.age);

        if (
          child.age === '' ||
          !Number.isInteger(age) ||
          age < 0 ||
          age > 30
        ) {
          alert(`Child ${index + 1}: Age must be between 0 and 30.`);
          valid = false;
        }
      });
    }

    // Father
    if (isEmpty(form.value.parents.fatherName)) {
      errors.value.fatherName = "Father's name is required.";
      valid = false;
    } else if (!isValidName(form.value.parents.fatherName)) {
      errors.value.fatherName = "Please enter a valid father's name.";
      valid = false;
    }

    // Father contact
    if (isEmpty(form.value.parents.fatherContact)) {
      errors.value.fatherContact = "Father's contact is required.";
      valid = false;
    } else if (!isValidPhone(form.value.parents.fatherContact)) {
      errors.value.fatherContact =
        "Father's contact must contain exactly 10 digits.";
      valid = false;
    }

    // Mother
    if (isEmpty(form.value.parents.motherName)) {
      errors.value.motherName = "Mother's name is required.";
      valid = false;
    } else if (!isValidName(form.value.parents.motherName)) {
      errors.value.motherName = "Please enter a valid mother's name.";
      valid = false;
    }

    // Mother contact - optional
    if (
      !isEmpty(form.value.parents.motherContact) &&
      !isValidPhone(form.value.parents.motherContact)
    ) {
      errors.value.motherContact =
        "Mother's contact must contain exactly 10 digits.";
      valid = false;
    }

    // Parent address
    if (isEmpty(form.value.parents.address)) {
      errors.value.parentsAddress = "Parents' address is required.";
      valid = false;
    }
  }


  // ============================================================
  // STEP 3 - WORK EXPERIENCE
  // ============================================================

  if (currentStep.value === 3) {

    form.value.workExperience.forEach((exp, index) => {

      // if (isEmpty(exp.company)) {
      //   alert(`Previous Work ${index + 1}: Company name is required.`);
      //   valid = false;
      // }

      // if (isEmpty(exp.position)) {
      //   alert(`Previous Work ${index + 1}: Position held is required.`);
      //   valid = false;
      // }

      // if (isEmpty(exp.address)) {
      //   alert(`Previous Work ${index + 1}: Company address is required.`);
      //   valid = false;
      // }

      // if (isEmpty(exp.startDate)) {
      //   alert(`Previous Work ${index + 1}: Start date is required.`);
      //   valid = false;
      // } else if (!isValidDate(exp.startDate)) {
      //   alert(`Previous Work ${index + 1}: Invalid start date.`);
      //   valid = false;
      // }

      // if (isEmpty(exp.endDate)) {
      //   alert(`Previous Work ${index + 1}: End date is required.`);
      //   valid = false;
      // } else if (!isValidDate(exp.endDate)) {
      //   alert(`Previous Work ${index + 1}: Invalid end date.`);
      //   valid = false;
      // }

      // End date cannot be before start date
      if (
        isValidDate(exp.startDate) &&
        isValidDate(exp.endDate) &&
        new Date(exp.endDate) < new Date(exp.startDate)
      ) {
        alert(
          `Previous Work ${index + 1}: End date cannot be before start date.`
        );
        valid = false;
      }

      //if (isEmpty(exp.reasonForLeaving)) {
      //   alert(
      //     `Previous Work ${index + 1}: Reason for leaving is required.`
      //   );
      //   valid = false;
      // }
    });
  }


  // ============================================================
  // STEP 4 - EDUCATION
  // ============================================================

  if (currentStep.value === 4) {

    form.value.education.forEach((edu, index) => {

      if (isEmpty(edu.certificate)) {
        alert(`Education ${index + 1}: Certificate/Degree is required.`);
        valid = false;
      }

      if (
        edu.certificate === 'Other' &&
        isEmpty(edu.otherCertificate)
      ) {
        alert(`Education ${index + 1}: Please specify the certificate.`);
        valid = false;
      }

      if (isEmpty(edu.school)) {
        alert(`Education ${index + 1}: School name is required.`);
        valid = false;
      }

      if (!isValidYear(edu.startYear)) {
        alert(
          `Education ${index + 1}: Start year must be between 1900 and ${new Date().getFullYear()}.`
        );
        valid = false;
      }

      if (!isValidYear(edu.endYear)) {
        alert(
          `Education ${index + 1}: End year must be between 1900 and ${new Date().getFullYear()}.`
        );
        valid = false;
      }

      if (
        isValidYear(edu.startYear) &&
        isValidYear(edu.endYear) &&
        Number(edu.endYear) < Number(edu.startYear)
      ) {
        alert(
          `Education ${index + 1}: End year cannot be before start year.`
        );
        valid = false;
      }
    });
  }


  // ============================================================
  // STEP 5 - CONTACTS / BANK / SECURITY / DOCUMENTS
  // ============================================================

  if (currentStep.value === 5) {

    // Emergency contact name
    if (isEmpty(form.value.emergencyContact.fullName)) {
      errors.value.emergencyFullName =
        'Emergency contact name is required.';
      valid = false;
    } else if (!isValidName(form.value.emergencyContact.fullName)) {
      errors.value.emergencyFullName =
        'Please enter a valid emergency contact name.';
      valid = false;
    }

    // Relationship
    if (isEmpty(form.value.emergencyContact.relationship)) {
      errors.value.emergencyRelationship =
        'Relationship is required.';
      valid = false;
    }

    // Emergency phone
    if (isEmpty(form.value.emergencyContact.phone)) {
      errors.value.emergencyPhone =
        'Emergency phone number is required.';
      valid = false;
    } 
    else if (!isValidPhone(form.value.emergencyContact.phone)) {
      errors.value.emergencyPhone =
        'Emergency phone must contain exactly 10 digits.';
      valid = false;
    }

    // Alternative phone - optional
    // if (
    //   !isEmpty(form.value.emergencyContact.altPhone) &&
    //   !isValidPhone(form.value.emergencyContact.altPhone)
    // ) {
    //   errors.value.emergencyAltPhone =
    //     'Alternative phone must contain exactly 10 digits.';
    //   valid = false;
    // }

    // Address
    // if (isEmpty(form.value.emergencyContact.address)) {
    //   errors.value.emergencyAddress =
    //     'Emergency contact address is required.';
    //   valid = false;
    // }

    // // Bank name
    // if (isEmpty(form.value.bank.bankName)) {
    //   errors.value.bankName = 'Bank name is required.';
    //   valid = false;
    // }

    // Branch
    // if (isEmpty(form.value.bank.branchName)) {
    //   errors.value.branchName = 'Branch name is required.';
    //   valid = false;
    // }

    // // Account number
    // if (isEmpty(form.value.bank.accountNumber)) {
    //   errors.value.accountNumber = 'Account number is required.';
    //   valid = false;
    // } else if (!isValidAccountNumber(form.value.bank.accountNumber)) {
    //   errors.value.accountNumber =
    //     'Account number must contain digits only.';
    //   valid = false;
    // }

    // // Account name
    // if (isEmpty(form.value.bank.accountName)) {
    //   errors.value.accountName =
    //     'Account holder name is required.';
    //   valid = false;
    // }

    // // Ghana Card number
    // if (isEmpty(form.value.security.ghanaCardNumber)) {
    //   errors.value.ghanaCardNumber =
    //     'Ghana Card number is required.';
    //   valid = false;
    // }

    // // Ghana Card expiry
    // if (isEmpty(form.value.security.ghanaCardExpiry)) {
    //   errors.value.ghanaCardExpiry =
    //     'Ghana Card expiry date is required.';
    //   valid = false;
    // }

    // // SSNIT
    // if (isEmpty(form.value.security.ssnitNumber)) {
    //   errors.value.ssnitNumber =
    //     'SSNIT number is required.';
    //   valid = false;
    // }

    // ------------------------------------------------------------
    // FILE VALIDATION
    // ------------------------------------------------------------

    // const documents = form.value.documents;

    // // CV
    // if (documents.cv) {
    //   if (
    //     !validateFile(
    //       documents.cv,
    //       'cv',
    //       [
    //         'application/pdf',
    //         'application/msword',
    //         'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    //       ]
    //     )
    //   ) {
    //     valid = false;
    //   }
    // } else if (!existingDocuments.value.cv) {
    //   errors.value.cv = 'CV is required.';
    //   valid = false;
    // }

    // // Ghana Card Front
    // if (documents.ghanaCardFront) {
    //   if (
    //     !validateFile(
    //       documents.ghanaCardFront,
    //       'ghanaCardFront'
    //     )
    //   ) {
    //     valid = false;
    //   }
    // } else if (!existingDocuments.value.ghanaCardFront) {
    //   errors.value.ghanaCardFront =
    //     'Ghana Card front is required.';
    //   valid = false;
    // }

    // // Ghana Card Back
    // if (documents.ghanaCardBack) {
    //   if (
    //     !validateFile(
    //       documents.ghanaCardBack,
    //       'ghanaCardBack'
    //     )
    //   ) {
    //     valid = false;
    //   }
    // } else if (!existingDocuments.value.ghanaCardBack) {
    //   errors.value.ghanaCardBack =
    //     'Ghana Card back is required.';
    //   valid = false;
    // }

    // // NHIS
    // if (documents.nhisCard) {
    //   if (
    //     !validateFile(
    //       documents.nhisCard,
    //       'nhisCard'
    //     )
    //   ) {
    //     valid = false;
    //   }
    // } else if (!existingDocuments.value.nhisCard) {
    //   errors.value.nhisCard =
    //     'NHIS card is required.';
    //   valid = false;
    // }
  }

  return valid;
};
  
  const prevStep = () => {
    if (currentStep.value > 1) {
      currentStep.value--;
    }
  };
  
  const updateChildrenFields = () => {
  const currentCount = form.value.marital.children.length;
  const newCount = form.value.marital.numberOfChildren;
  
  if (newCount > currentCount) {
    // Add new children
    for (let i = currentCount; i < newCount; i++) {
      form.value.marital.children.push({ fullName: '', age: '' });
    }
  } else if (newCount < currentCount) {
    // Remove extra children
    form.value.marital.children.splice(newCount, currentCount - newCount);
  }
};

const addChild = () => {
  form.value.marital.children.push({ fullName: '', age: '' });
  form.value.marital.numberOfChildren = form.value.marital.children.length;
};

const removeChild = (index) => {
  form.value.marital.children.splice(index, 1);
  form.value.marital.numberOfChildren = form.value.marital.children.length;
};

  const addWorkExperience = () => {
    form.value.workExperience.push({
      company: '',
      position: '',
      address: '',
      contact: '',
      startDate: '',
      endDate: '',
      reasonForLeaving: ''
    });
  };
  
  const removeWorkExperience = (index) => {
    form.value.workExperience.splice(index, 1);
  };
  
  const addEducation = () => {
    form.value.education.push({
      certificate: '',
      otherCertificate: '',
      school: '',
      startYear: '',
      endYear: ''
    });
  };
  
  const removeEducation = (index) => {
    form.value.education.splice(index, 1);
  };
  
const handleFileUpload = (field, event) => {
  const file = event.target.files?.[0];

  if (!file) {
    form.value.documents[field] = null;
    return;
  }

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

  if (file.size > MAX_FILE_SIZE) {
    errors.value[field] = 'File size must not exceed 2 MB.';

    // Clear selected file
    form.value.documents[field] = null;

    // Clear input
    event.target.value = '';

    return;
  }

  // CV allowed types
  if (field === 'cv') {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      errors.value.cv = 'CV must be a PDF, DOC, or DOCX file.';
      form.value.documents[field] = null;
      event.target.value = '';
      return;
    }
  }

  // ID / NHIS documents
  if (
    field === 'ghanaCardFront' ||
    field === 'ghanaCardBack' ||
    field === 'nhisCard'
  ) {
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp'
    ];

    if (!allowedTypes.includes(file.type)) {
      errors.value[field] =
        'File must be an image or PDF.';
      form.value.documents[field] = null;
      event.target.value = '';
      return;
    }
  }

  // Valid file
  errors.value[field] = '';
  form.value.documents[field] = file;
};

  const createFormData = (formObj) => {
  const formData = new FormData();

  // Personal info
  Object.keys(formObj.personal).forEach(key => {
    formData.append(`personal[${key}]`, formObj.personal[key]);
  });

  // Marital info
  Object.keys(formObj.marital).forEach(key => {
    if (key === 'children') {
      formObj.marital.children.forEach((child, i) => {
        Object.keys(child).forEach(cKey => {
          formData.append(`marital[children][${i}][${cKey}]`, child[cKey]);
        });
      });
    } else {
      formData.append(`marital[${key}]`, formObj.marital[key]);
    }
  });

  // Parents
  Object.keys(formObj.parents).forEach(key => {
    formData.append(`parents[${key}]`, formObj.parents[key]);
  });

  // Work Experience (array)
  formObj.workExperience.forEach((exp, i) => {
    Object.keys(exp).forEach(key => {
      formData.append(`workExperience[${i}][${key}]`, exp[key]);
    });
  });

  // Education (array)
  formObj.education.forEach((edu, i) => {
    Object.keys(edu).forEach(key => {
      formData.append(`education[${i}][${key}]`, edu[key]);
    });
  });

  // Emergency contact
  Object.keys(formObj.emergencyContact).forEach(key => {
    formData.append(`emergencyContact[${key}]`, formObj.emergencyContact[key]);
  });

  // Bank info
  Object.keys(formObj.bank).forEach(key => {
    formData.append(`bank[${key}]`, formObj.bank[key]);
  });

  // Security info
  Object.keys(formObj.security).forEach(key => {
    formData.append(`security[${key}]`, formObj.security[key]);
  });

  // Documents (files)
  Object.keys(formObj.documents).forEach(key => {
    if (formObj.documents[key]) {
      formData.append(`documents[${key}]`, formObj.documents[key]);
    }
  });

  return formData;
};

// ────────────────────────────────────────────────
// Draft save / update per step + draft loading
// ────────────────────────────────────────────────

const employeeId = ref(null);
const isLoadingDraft = ref(false);
const savingStep = ref(null);
const stepMessages = ref({});
const existingDocuments = ref({
  cv: null,
  ghanaCardFront: null,
  ghanaCardBack: null,
  nhisCard: null
});

// Maps the visible wizard step number to the backend "step" key
const stepKeyMap = {
  1: 'personal',
  2: 'family',
  3: 'work',
  4: 'education',
  5: 'contacts'
};

const buildStepFormData = (stepKey) => {
  const formData = new FormData();
  formData.append('action', 'save_step');
  formData.append('step', stepKey);

  if (stepKey === 'personal') {
    Object.keys(form.value.personal).forEach(key => {
      formData.append(`personal[${key}]`, form.value.personal[key] ?? '');
    });
    // marital status travels with personal so the employee record always
    // has a consistent status even if step 2 hasn't been saved yet
    formData.append('marital[status]', form.value.marital.status ?? '');
  }

  if (stepKey === 'family') {
    Object.keys(form.value.marital).forEach(key => {
      if (key === 'children') {
        form.value.marital.children.forEach((child, i) => {
          Object.keys(child).forEach(cKey => {
            formData.append(`marital[children][${i}][${cKey}]`, child[cKey]);
          });
        });
      } else {
        formData.append(`marital[${key}]`, form.value.marital[key] ?? '');
      }
    });
    Object.keys(form.value.parents).forEach(key => {
      formData.append(`parents[${key}]`, form.value.parents[key] ?? '');
    });
  }

  if (stepKey === 'work') {
    form.value.workExperience.forEach((exp, i) => {
      Object.keys(exp).forEach(key => {
        formData.append(`workExperience[${i}][${key}]`, exp[key] ?? '');
      });
    });
  }

  if (stepKey === 'education') {
    form.value.education.forEach((edu, i) => {
      Object.keys(edu).forEach(key => {
        formData.append(`education[${i}][${key}]`, edu[key] ?? '');
      });
    });
  }

  if (stepKey === 'contacts') {
    Object.keys(form.value.emergencyContact).forEach(key => {
      formData.append(`emergencyContact[${key}]`, form.value.emergencyContact[key] ?? '');
    });
    Object.keys(form.value.bank).forEach(key => {
      formData.append(`bank[${key}]`, form.value.bank[key] ?? '');
    });
    Object.keys(form.value.security).forEach(key => {
      formData.append(`security[${key}]`, form.value.security[key] ?? '');
    });
    Object.keys(form.value.documents).forEach(key => {
      if (form.value.documents[key]) {
        formData.append(`documents[${key}]`, form.value.documents[key]);
      }
    });
  }

  return formData;
};

const saveStep = async (stepNumber) => {
  const stepKey = stepKeyMap[stepNumber];
  if (!stepKey) return;

  savingStep.value = stepNumber;
  stepMessages.value = { ...stepMessages.value, [stepNumber]: null };

  try {
    const formData = buildStepFormData(stepKey);
    const response = await api.post('Staff.php', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    if (response.data?.success === false) {
      stepMessages.value = {
        ...stepMessages.value,
        [stepNumber]: { type: 'error', text: response.data.message || 'Could not save this step. Please try again.' }
      };
      return;
    }

    if (response.data?.employeeId) {
      employeeId.value = response.data.employeeId;
    }

    if (stepKey === 'contacts' && response.data?.documents) {
      existingDocuments.value = { ...existingDocuments.value, ...response.data.documents };
    }

    stepMessages.value = {
      ...stepMessages.value,
      [stepNumber]: { type: 'success', text: 'Saved.' }
    };
  } catch (error) {
    console.error('Save step failed:', error);
    const serverMsg = error.response?.data?.message;
    stepMessages.value = {
      ...stepMessages.value,
      [stepNumber]: { type: 'error', text: serverMsg || 'Failed to save. Check your connection and try again.' }
    };
  } finally {
    savingStep.value = null;
  }
};

const loadDraft = async () => {
  isLoadingDraft.value = true;
  try {
    const response = await api.get('Staff.php', { params: { action: 'load_draft' } });
    const data = response.data;

    if (!data?.success || !data.employeeId) {
      return;
    }

    employeeId.value = data.employeeId;

    if (data.personal) {
      Object.assign(form.value.personal, data.personal);
    }

    if (data.marital) {
      const { children, ...maritalRest } = data.marital;
      Object.assign(form.value.marital, maritalRest);
      if (Array.isArray(children) && children.length > 0) {
        form.value.marital.children = children;
        form.value.marital.hasChildren = true;
        form.value.marital.numberOfChildren = children.length;
      }
    }

    if (data.parents) {
      Object.assign(form.value.parents, data.parents);
    }

    if (Array.isArray(data.workExperience) && data.workExperience.length > 0) {
      form.value.workExperience = data.workExperience;
    }

    if (Array.isArray(data.education) && data.education.length > 0) {
      form.value.education = data.education;
    }

    if (data.emergencyContact) {
      Object.assign(form.value.emergencyContact, data.emergencyContact);
    }

    if (data.bank) {
      Object.assign(form.value.bank, data.bank);
    }

    if (data.security) {
      Object.assign(form.value.security, data.security);
    }

    if (data.documents) {
      existingDocuments.value = { ...existingDocuments.value, ...data.documents };
    }
  } catch (error) {
    // No draft yet, or the user isn't authenticated for this endpoint — fail quietly,
    // the form simply starts blank.
    console.warn('No draft loaded:', error);
  } finally {
    isLoadingDraft.value = false;
  }
};


  
const isSubmitting = ref(false);


const clearSignature = () => {
  if (signaturePad) {
    signaturePad.clear();
  }
};

onMounted(() => {
  loadDraft();
  nextTick(() => {
    if (signatureCanvas.value) {
      signaturePad = new SignaturePad(signatureCanvas.value);
    }
  });
});
const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};
const showSuccessModal = ref(false);

const submitForm = async () => {
   if (isSubmitting.value) {
    console.log("Already submitting – ignoring duplicate click");
    return;
  }

  // Validate the final form before submitting
  const validationResult = validateAllSteps();

  if (!validationResult) {
    alert('Please correct the validation errors before submitting.');
    return;
  }

  if (!signaturePad || signaturePad.isEmpty()) {
    alert("Please sign before submitting");
    return;
  }

  isSubmitting.value = true;

  try {
    const formData = createFormData(form.value);
    formData.append('action', 'submit');
    const signatureDataURL = signaturePad.toDataURL();
    const signatureBlob = dataURLtoBlob(signatureDataURL);

    formData.append("signature", signatureBlob, "signature.png");

const response = await api.post("Staff.php", formData, {
  headers: { "Content-Type": "multipart/form-data" }
});

// Check the actual payload, not just that the request didn't throw
if (response.data?.success === false || response.data?.status === "error") {
  console.error("Server reported failure:", response.data);
  alert(response.data.message || "Submission failed. Please check your details and try again.");
  return; // stop here — don't reset form or show success modal
}

console.log("Success:", response.data);
resetForm();
showSuccessModal.value = true;

} catch (error) {
  console.error("Submit failed:", error);

  if (error.response) {
    // Server responded with a non-2xx status
    const serverMsg = error.response.data?.message;
    alert(serverMsg || `Server error (${error.response.status}). Please try again.`);
  } else if (error.request) {
    // Request made but no response received (network/timeout)
    alert("No response from server. Check your connection and try again.");
  } else {
    alert("Failed to submit. Check console or try again.");
  }
} finally {
  isSubmitting.value = false;
}
};

const validateAllSteps = () => {
  const originalStep = currentStep.value;

  for (let step = 1; step <= 5; step++) {
    currentStep.value = step;

    if (!validateCurrentStep()) {
      return false;
    }
  }

  currentStep.value = originalStep;

  return true;
};

const resetForm = () => {
  form.value = {
    personal: {
      firstName: '',
      middleName: '',
      lastName: '',
      gender: '',
      dob: '',
      placeOfBirth: '',
      nationality: '',
      address: '',
      phone: '',
      email: ''
    },
    marital: {
      status: '',
      spouseName: '',
      hasChildren: false,
      numberOfChildren: 1,
      children: [{ fullName: '', age: '' }]
    },
    parents: {
      fatherName: '',
      fatherContact: '',
      motherName: '',
      motherContact: '',
      address: ''
    },
    workExperience: [
      {
        company: '',
        position: '',
        address: '',
        contact: '',
        startDate: '',
        endDate: '',
        reasonForLeaving: ''
      }
    ],
    education: [
      {
        certificate: '',
        otherCertificate: '',
        school: '',
        startYear: '',
        endYear: ''
      }
    ],
    emergencyContact: {
      fullName: '',
      relationship: '',
      phone: '',
      altPhone: '',
      address: ''
    },
    bank: {
      bankName: '',
      branchName: '',
      accountNumber: '',
      accountName: ''
    },
    security: {
      ghanaCardNumber: '',
      ghanaCardExpiry: '',
      ssnitNumber: ''
    },
    documents: {
      cv: null,
      ghanaCardFront: null,
      ghanaCardBack: null,
      nhisCard: null
    }
  };

  currentStep.value = 1;
  employeeId.value = null;
  stepMessages.value = {};
  existingDocuments.value = {
    cv: null,
    ghanaCardFront: null,
    ghanaCardBack: null,
    nhisCard: null
  };
};
  </script>
  
<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-box {
  background: white;
  padding: 24px 32px;
  border-radius: 8px;
  text-align: center;
  max-width: 400px;
}
.modal-box h3 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #035a76;
}
.modal-box button {
  margin-top: 16px;
  padding: 8px 20px;
  background: #76ba51;
  color: white;
  border-radius: 6px;
  border: none;
  cursor: pointer;
}
.modal-box button:hover {
  background: #76ba51e6;
}
</style>